import { MouseEvent } from 'react';

import { isRelative } from '@sureapp/wl-toggle-common/dist/utils/url';
import clsx from 'clsx';
import camelCase from 'lodash/camelCase';
import { NavLink, useRouteMatch } from 'react-router-dom';

import classes from './NavBar.module.css';

export type TNavMenuItem = {
  dataTestId?: string;
  dropdownLinks?: Omit<TNavMenuItem, 'onToggleMenu'>[];
  exact?: boolean;
  isDropdownMenu?: boolean;
  onClick?(): void;
  onToggleMenu(): void;
  title: string;
  depth?: number;
  to: string;
  target?: string;
};

function NavMenuItem({
  dataTestId,
  dropdownLinks,
  exact = false,
  isDropdownMenu,
  onClick,
  onToggleMenu,
  title,
  depth = 0,
  to,
  target,
}: TNavMenuItem) {
  const match = useRouteMatch();
  function handleClick(e: MouseEvent<HTMLAnchorElement>) {
    if (e.currentTarget.href === window.location.href) {
      e.preventDefault();
      onToggleMenu();
    }
  }

  const testId = `NavBarMenu_${depth > 0 ? 'sub' : ''}link_${dataTestId}`;

  if (isDropdownMenu) {
    return (
      <li className={classes.menu_list_item_dropdown} data-testid={testId}>
        <button className={classes.menu_list_item_link} type="button">
          {title}
          <i
            className={clsx(
              classes.menu_list_item_dropdown_icon,
              'fas',
              'fa-caret-down'
            )}
          />
        </button>
        <ul
          data-testid={`dropdown-${camelCase(title)}`}
          className={classes.menu_list_item_dropdown_content}
        >
          <button className={classes.menu_list_item_link} type="button">
            {title}
            <i
              className={clsx(
                classes.menu_list_item_dropdown_icon,
                'fas',
                'fa-caret-down'
              )}
            />
          </button>
          {dropdownLinks &&
            dropdownLinks.map(
              (
                item: Omit<
                  TNavMenuItem,
                  'dropdownLinks' | 'isDropdownMenu' | 'onToggleMenu'
                >
              ) => (
                <NavMenuItem
                  key={item.to || item.title}
                  depth={depth + 1}
                  {...{ ...item, onToggleMenu }}
                />
              )
            )}
        </ul>
      </li>
    );
  }

  function isMFEAppLink(href: string) {
    // Leaving renters out for now as it's rendered by the auto app.
    return ['/homeowners', '/auto'].includes(href);
  }

  return (
    <li className={classes.menu_list_item}>
      {isRelative(to) && !isMFEAppLink(to) ? (
        <NavLink
          to={to}
          className={classes.menu_list_item_link}
          activeClassName={classes.is_active}
          onClick={handleClick}
          data-testid={testId}
          exact={exact}
          target={target}
        >
          {title}
        </NavLink>
      ) : (
        <a
          href={to}
          className={clsx(classes.menu_list_item_link, {
            [classes.is_active]: to?.endsWith(match.path),
          })}
          onClick={onClick || handleClick}
          data-testid={testId}
          target={target}
        >
          {title}
        </a>
      )}
    </li>
  );
}

export default NavMenuItem;
